import { FolderIcon } from "@/Icons/v2/Library/FolderIcon";
import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { LibraryPageContext } from "@/pages/library_v2/components/filesView/context";
import {
  FileStatus,
  type LibraryFile,
} from "@/pages/library_v2/components/filesView/types";
import { isSafeFileName } from "@/pages/library_v2/components/filesView/utils";
import { useTrans } from "@/translations";

import { useCallback, useContext, useMemo, useState } from "react";

import { PopoverPanel } from "@headlessui/react";
import type { FILE_LOCATION_TYPE } from "server/bucketApi/library/constants.js";
import { twMerge } from "tailwind-merge";

type Props = {
  file: LibraryFile;
};

export const MoveToFolderPopup: React.FC<Props> = ({ file }) => {
  const { t } = useTrans();
  const { setGlobalAlert } = useGlobalAlert();
  const { currentTerminal } = useTerminalsState();
  const { folders, refetchFiles, refetchFolders, createFolder } =
    useContext(LibraryPageContext);
  const [newFolderName, setNewFolderName] = useState("");

  const isNewFolderNameValid = useMemo(
    () => isSafeFileName(newFolderName),
    [newFolderName],
  );

  const { mutate: moveFile } = trpc.library.moveFile.useMutation({
    onSuccess: () => {
      refetchFiles();
      setGlobalAlert({
        type: "success",
        message: "library:fileStatusChangeSuccessful",
      });
    },
    onError: (error) => {
      setGlobalAlert({
        type: "error",
        message: "errors:documentStatusChangeFailed",
      });
      console.error("Error changing document status:", error);
    },
  });

  const onMoveFileToFolder = useCallback(
    async (newFolderName: string) => {
      await moveFile({
        terminalId: currentTerminal?.id!,
        filename: `${file.name}.${file.format}`,
        folderNameFrom: file.folderName || "",
        folderNameTo: newFolderName,
        from: FileStatus.PUBLISHED as string as FILE_LOCATION_TYPE,
        to: FileStatus.PUBLISHED as string as FILE_LOCATION_TYPE,
      });
    },
    [moveFile, currentTerminal, file],
  );

  const onAddFolder = useCallback(async () => {
    const trimmed = newFolderName.trim();
    if (!trimmed || !isSafeFileName(trimmed)) return;

    await createFolder({
      terminalId: currentTerminal?.id!,
      folderName: trimmed,
    });

    setNewFolderName("");

    await refetchFolders();
  }, [createFolder, currentTerminal?.id, newFolderName, refetchFolders]);

  return (
    <PopoverPanel
      anchor={{ to: "bottom end", gap: 8 }}
      className="border-grey3 z-[10000] w-[270px] rounded-lg border bg-white p-3 shadow-lg"
    >
      {({ close }) => (
        <>
          <div className="pb-2 text-[11px] font-bold tracking-[0.06em] text-[#9a958f]">
            {t("libraryPage:fileListMoveToFolderTitle")}
          </div>

          <button
            type="button"
            onClick={() => {
              onMoveFileToFolder("");
              close();
            }}
            className={twMerge(
              "flex w-full cursor-pointer items-center gap-[9px] rounded-[7px] px-[9px] py-2 text-left text-[13px] font-semibold text-[#1D1C1C]",
              !file.folderName ? "bg-[#F3F5AE]" : "hover:bg-grey1",
            )}
          >
            <FolderIcon size={14} color="#6F6C6C" />
            <span className="min-w-0 flex-1 truncate">
              {t("libraryPage:folderListPublished")}
            </span>
          </button>

          <div className="my-[2px] ml-[17px] flex flex-col border-l border-[#F1EAEA] pl-[9px]">
            {folders.map((folder) => {
              const isCurrent = folder.name === file.folderName;
              return (
                <button
                  key={folder.name}
                  type="button"
                  onClick={() => {
                    onMoveFileToFolder(folder.name);
                    close();
                  }}
                  className={twMerge(
                    "group/mv flex w-full cursor-pointer items-center gap-[8px] rounded-[7px] px-[9px] py-[7px] text-left text-[12.5px] text-[#1D1C1C]",
                    isCurrent
                      ? "bg-[#F3F5AE] font-semibold"
                      : "hover:bg-grey1 font-medium",
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                  <span className="text-grey6 flex-none text-[11px] font-semibold opacity-0 transition-opacity group-hover/mv:opacity-100 group-focus/mv:opacity-100">
                    {t("libraryPage:fileListActionMove")}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-[11px] flex flex-col gap-[3px] border-t border-[#F1EAEA] pt-[11px]">
            <div className="flex gap-[7px]">
              <input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onAddFolder();
                }}
                placeholder={t("libraryPage:folderListNewFolder")}
                className={twMerge(
                  "min-w-0 flex-1 rounded border px-[9px] py-[7px] text-[12.5px] outline-none",
                  !isNewFolderNameValid && newFolderName.length
                    ? "border-red-500"
                    : "border-grey3",
                )}
              />
              <button
                type="button"
                onClick={onAddFolder}
                disabled={!isNewFolderNameValid}
                className="cursor-pointer rounded bg-black px-[12px] py-[7px] text-[12px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("libraryPage:folderListNewFolderAdd")}
              </button>
            </div>
            {!isNewFolderNameValid && !!newFolderName.length && (
              <span className="text-[11px] font-normal text-red-500">
                {t("libraryPage:folderNameInvalidChars")}
              </span>
            )}
          </div>
        </>
      )}
    </PopoverPanel>
  );
};
